import React from "react";
import { Typography, Box, Chip, Grid, Tooltip } from "@mui/material";
import Card from "./UIKit/Card";
import { Link } from "react-router-dom";
import {
  Person as PersonIcon,
  Article as ArticleIcon,
  VisibilityOff as VisibilityOffIcon,
  School as SchoolIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ModuleDisplayCard = ({
  module,
  showDate = true,
  showAuthor = true,
  showStats = true,
  variant = "default", // 'default', 'compact', 'detailed', 'fullWidth'
  showCourseInfo = false,
  customButtons = null,
  onView = null,
  onEdit = null,
  onDelete = null,
  canEdit = false,
  sx = {},
  ...props
}) => {
  const { t } = useTranslation(["modules", "common"]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Card
      component={Link}
      to={`/modules/${module.slug || module._id}`}
      clickable
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        height: "100%",
        textDecoration: "none",
        "&:hover": {
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
        ...sx
      }}
      {...props}
    >
      {/* Draft indicator - TOP RIGHT */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {module.isDraft && (
          <Tooltip title={t('modulesPage.status.draft')}>
            <VisibilityOffIcon
              fontSize="small"
              color="action"
            />
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={variant === "fullWidth" ? 3 : 2}>
        <Grid item xs={12}>
          {/* Заголовок модуля */}
          <Box
            display="flex"
            alignItems="flex-start"
            mb={variant === "fullWidth" ? 2 : 1}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant={variant === "fullWidth" ? "h6" : "subtitle1"}
                fontWeight="bold"
                sx={{
                  lineHeight: 1.2,
                  mb: 0.5,
                  color: 'text.primary',
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {module.title}
              </Typography>

              {/* Автор модуля */}
              {showAuthor && module.author && (
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <PersonIcon
                    sx={{
                      mr: 0.5,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      fontSize: "0.75rem",
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {t("common:author")}: {module.author?.userMeta?.fullName || t("moduleCard.noAuthor")}
                  </Typography>
                </Box>
              )}

              {/* Статистика модуля */}
              {showStats && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mb: variant === "fullWidth" ? 2 : 1.5,
                    alignItems: "center",
                  }}
                >
                  {/* Количество уроков */}
                  <Chip
                    icon={<ArticleIcon sx={{ fontSize: "0.75rem" }} />}
                    label={`${module.lessons?.length || 0} ${t('modulesPage.stats.lessons')}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: "24px",
                      "& .MuiChip-label": {
                        fontSize: "0.7rem",
                        px: 0.5,
                      },
                    }}
                  />

                  {/* Курсы, где используется модуль */}
                  {module.courses && module.courses.length > 0 && (
                    <Chip
                      icon={<SchoolIcon sx={{ fontSize: "0.75rem" }} />}
                      label={`${module.courses.length} ${t('modulesPage.stats.usedInCourses')}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        height: "24px",
                        "& .MuiChip-label": {
                          fontSize: "0.7rem",
                          px: 0.5,
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Описание модуля */}
          {module.description && (
            <Typography
              variant={variant === "fullWidth" ? "body1" : "body2"}
              color="textSecondary"
              sx={{
                mb: variant === "fullWidth" ? 3 : 2.5,
                lineHeight: 1.4,
                display: variant === "fullWidth" ? "block" : "-webkit-box",
                WebkitLineClamp: variant === "fullWidth" ? "unset" : 2,
                WebkitBoxOrient: variant === "fullWidth" ? "unset" : "vertical",
                overflow: variant === "fullWidth" ? "visible" : "hidden",
              }}
            >
              {module.description}
            </Typography>
          )}

          {/* Информация о курсах */}
          {showCourseInfo && module.courses && module.courses.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 1, fontWeight: 500, fontSize: "0.8rem" }}
              >
                {t('modulesPage.stats.usedInCourses')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {module.courses.slice(0, 2).map((course) => (
                  <Chip
                    key={course._id}
                    label={course.title.length > 15 ? course.title.substring(0, 15) + '...' : course.title}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: "22px",
                      "& .MuiChip-label": {
                        fontSize: "0.7rem",
                        px: 0.5,
                      },
                    }}
                  />
                ))}
                {module.courses.length > 2 && (
                  <Chip
                    label={`+${module.courses.length - 2}`}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: "22px",
                      "& .MuiChip-label": {
                        fontSize: "0.7rem",
                        px: 0.5,
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Кнопки действий */}
          {customButtons && (
            <Box
              sx={{
                mt: variant === "fullWidth" ? 2 : 1.5,
                display: "flex",
                justifyContent: "flex-end",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {customButtons}
            </Box>
          )}

          {/* Дата создания */}
          {showDate && module.createdAt && (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{
                mt: "auto",
                pt: variant === "fullWidth" ? 1.5 : 1,
                borderTop: "1px solid",
                borderColor: "divider",
                textAlign: "right",
                fontSize: "0.75rem",
              }}
            >
              {formatDate(module.createdAt)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default ModuleDisplayCard;
